import { type FC, type PropsWithChildren, useEffect, useRef, useState } from 'react';

import { stickyContainerRecipe } from './StickyHeader.css';
import { useWindow } from '../../providers/mediaQuery';
import { getClassNames } from '../../util/css';

type ScrollDirection = 'up' | 'down' | 'top';

const sparkyStickyHeaderClassName = 'sparky-sticky-header';

export const StickyHeader: FC<PropsWithChildren> = ({ children }) => {
  const isMounted = useWindow();
  const ref = useRef<HTMLDivElement>(null);
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('top');
  const [headerHeight, setHeight] = useState<number>();

  let previousScroll = isMounted ? window.scrollY : 0;

  const handleScroll = () => {
    const headerHeight = ref.current?.offsetHeight;
    if (!headerHeight) {
      return;
    }

    const currentScroll = window.scrollY;
    const isScrollingUp = previousScroll > currentScroll;

    previousScroll = currentScroll;

    setScrollDirection(prevDirection => {
      if (
        (isScrollingUp && currentScroll === 0) ||
        (!isScrollingUp && currentScroll < headerHeight) ||
        (prevDirection === 'top' && currentScroll < headerHeight)
      ) {
        return 'top';
      }

      return isScrollingUp ? 'up' : 'down';
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(entries => {
      setHeight(entries[0]?.contentRect.height);
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classNames = getClassNames(
    sparkyStickyHeaderClassName,
    stickyContainerRecipe({ scrollDirection }),
  );

  return (
    <div style={headerHeight ? { height: headerHeight } : {}}>
      <div
        ref={ref}
        className={classNames}
        style={scrollDirection !== 'up' ? { top: `-${headerHeight}px` } : undefined}>
        {children}
      </div>
    </div>
  );
};

StickyHeader.displayName = 'StickyHeader';
